export function exportStamp() {
  const d = new Date();
  const p = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}`;
}

export function exportFilename(base, ext, user) {
  const who = user
    ? (`${user.first_name || ""}-${user.last_name || ""}`.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "user")
    : "user";
  return `${base}-${exportStamp()}-${who}.${ext}`;
}

export function snapTo5(v) {
  if (!v) return v;
  const [d, t] = v.split("T");
  if (!t) return v;
  let [h, m] = t.split(":").map(Number);
  m = Math.round(m / 5) * 5;
  if (m === 60) { m = 0; h = (h + 1) % 24; }
  return `${d}T${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function fmtHM(hours) {
  const tm = Math.max(0, Math.round((Number(hours) || 0) * 60));
  return `${Math.floor(tm / 60)}:${String(tm % 60).padStart(2, "0")}`;
}

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  if (isNaN(d.getTime())) return String(iso);
  return `${DOW[d.getDay()]} ${d.getDate()} ${MON[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
}

export function fmtDateShort(iso) {
  if (!iso) return "";
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  if (isNaN(d.getTime())) return String(iso);
  return `${d.getDate()} ${MON[d.getMonth()]}`;
}

export function fmtDT(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  const date = `${DOW[d.getDay()]} ${d.getDate()} ${MON[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
  return `${date} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function tzOffsetMinutes(tz, date) {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  });
  const parts = Object.fromEntries(dtf.formatToParts(date).map((p) => [p.type, p.value]));
  const asUTC = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour % 24, +parts.minute, +parts.second);
  return Math.round((asUTC - date.getTime()) / 60000);
}

export function fmtTzAbbr(tz, dateStr) {
  if (!tz) return "";
  const abbr = tz === "UTC" ? "UTC" : tz.split("/").map((s) => s[0].toUpperCase() + s.slice(1, 3)).join("/");
  let off = "";
  try {
    const d = dateStr ? new Date(`${dateStr}T12:00:00Z`) : new Date();
    const mins = tzOffsetMinutes(tz, d);
    const sign = mins < 0 ? "-" : "+";
    const h = Math.floor(Math.abs(mins) / 60);
    const m = Math.abs(mins) % 60;
    off = ` UTC${sign}${h}${m ? `:${String(m).padStart(2, "0")}` : ""}`;
  } catch { /* unknown timezone */ }
  return `${abbr}${off}${isDST(tz, dateStr) ? " DST" : ""}`;
}

export function isDST(tz, dateStr) {
  try {
    const year = dateStr ? +dateStr.slice(0, 4) : new Date().getFullYear();
    const jan = tzOffsetMinutes(tz, new Date(Date.UTC(year, 0, 1, 12)));
    const jul = tzOffsetMinutes(tz, new Date(Date.UTC(year, 6, 1, 12)));
    if (jan === jul) return false;
    const d = dateStr ? new Date(`${dateStr}T12:00:00Z`) : new Date();
    return tzOffsetMinutes(tz, d) > Math.min(jan, jul);
  } catch { return false; }
}

export function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function todayLocal() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function tzOffset(tz, dateStr) {
  try {
    const d = dateStr ? new Date(`${dateStr}T12:00:00`) : new Date();
    const part = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "longOffset" })
      .formatToParts(d).find((p) => p.type === "timeZoneName");
    const v = (part?.value || "GMT").replace("GMT", "");
    return v === "" ? "+00:00" : v;
  } catch { return ""; }
}

export function zoneShort(tz) {
  if (!tz) return "";
  const parts = tz.split("/");
  if (parts.length < 2) return tz;
  const region = parts[0][0].toUpperCase() + parts[0].slice(1, 3).toLowerCase();
  const city = (parts[parts.length - 1] || "").replace(/_/g, " ").split(" ")[0].slice(0, 3).toUpperCase();
  return `${region}/${city}`;
}

export function yesterdayLocal() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function fmtDowOrd(iso) {
  if (!iso) return "";
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  if (isNaN(d.getTime())) return String(iso);
  const n = d.getDate();
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${DOW[d.getDay()]} ${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}
